import type Stripe from 'stripe-latest';

import { SECURITY } from '../featureFlags/flagNames';
import type { FlagName } from '../featureFlags/types';
import type { StripePriceLimit } from '../model-typings/stripepricelimits';

import {
  apiPriceToPlanNameMap,
  ENTERPRISE_COMPLIANCE,
  ENTERPRISE_PLATFORM,
  ENTERPRISE_RELEASE_MANAGEMENT,
  fossaPlanMap,
  fossaPlanNames,
  FREE_PLAN_NAME,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
  MAXIMUM_BUSINESS_SEATS,
  MINIMUM_BUSINESS_SEATS,
  MINIMUM_BUSINESS_SEATS_STEP,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_COMPLIANCE_PLAN_NAME,
  PREMIUM_PER_REVISION_PLAN_NAME,
  STRIPE_PRICE_PREFIX,
} from './consts';
import { TIER_FEATURE_MAP } from './tierFeatures';
import type { FossaPlanName, FossaSubscriptionWithPlanName, StaticPlanName } from './consts';

/**
 * Parameters to be used when fetching plan info for an org.
 */
type FetchPlanInfo = {
  /**
   * The fossa plan name to be checked if any.
   */
  plan?: FossaPlanName;
  /**
   * The Stripe price limit to check for if any.
   */
  stripePriceLimit?: StripePriceLimit | null;
};

export const isNonLegacyPlan = (plan: FossaPlanName) =>
  [
    FREE_PLAN_NAME,
    PREMIUM_COMPLIANCE_PLAN_NAME,
    PREMIUM_COMP_SEC_PLAN_NAME,
    PREMIUM_PER_REVISION_PLAN_NAME,
    ENTERPRISE_COMPLIANCE,
    ENTERPRISE_PLATFORM,
    ENTERPRISE_RELEASE_MANAGEMENT,
  ].includes(plan);

export const isDynamicPlan = (plan: string | null | undefined): boolean =>
  (plan && plan.startsWith(STRIPE_PRICE_PREFIX) && !apiPriceToPlanNameMap[plan]) === true;

/**
 * given a stripe plan, returns subscription details
 */
export function getSubscription({
  plan = FREE_PLAN_NAME,
  stripePriceLimit,
}: FetchPlanInfo): FossaSubscriptionWithPlanName {
  const freePlanInfo = fossaPlanMap[FREE_PLAN_NAME];
  if (isDynamicPlan(plan)) {
    const privateRepos = stripePriceLimit?.limits.projectCount || freePlanInfo.privateRepos;
    return {
      label: 'Dynamic Plan',
      description: 'Stripe dynamic plan. Information is managed in Stripe.',
      planName: plan,
      privateRepos: privateRepos === -1 ? 'Unlimited' : privateRepos,
    } as FossaSubscriptionWithPlanName;
  }

  if (!fossaPlanNames.includes(plan as StaticPlanName)) {
    throw new Error('FOSSA subscription plan not found');
  }

  return {
    ...fossaPlanMap[plan],
    planName: plan,
  } as FossaSubscriptionWithPlanName;
}

/**
 * Retrieves the price of FOSSA plan based on plan name. This is only used in the internal tools and admin pages.
 * Return $0 here is ok for dynamic plans for now since they'll not be managed by internal tools.
 */
export const getSubscriptionPrice = (stripePlan: FossaPlanName = FREE_PLAN_NAME): number => {
  const mappedPlanInfo = fossaPlanMap[stripePlan];
  if (!mappedPlanInfo) {
    return 0;
  }

  return mappedPlanInfo.price;
};

const enterprisePlans = new Set([ENTERPRISE_PLATFORM, ENTERPRISE_COMPLIANCE, ENTERPRISE_RELEASE_MANAGEMENT]);

/**
 * Helper function to see if an organization's is an enterprise plan. This can use one of the old static plan names
 * or the new dynamic price from Stripe. Both parameters are optional.
 *
 * @param params Parameters to use to check for enterprise tier.
 * @param {FossaPlanName} params.plan - The fossa plan name to be checked (optional)
 * @param {StripePriceLimit} params.stripePriceLimit - The Stripe price limit to check for (optional)
 */
export const isEnterpriseTier = (params: FetchPlanInfo | undefined | null): boolean => {
  if (!params || !params.plan) {
    return false;
  }

  if (params.plan && enterprisePlans.has(params.plan)) {
    return true;
  }

  if (params.stripePriceLimit && params.plan === params.stripePriceLimit.priceId) {
    return params.stripePriceLimit.type === 'ENTERPRISE';
  }

  return false;
};

const businessPlans = new Set([
  PREMIUM_COMPLIANCE_PLAN_NAME,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_PER_REVISION_PLAN_NAME,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
]);

/**
 * Helper function to see if an organization's `stripe_plan` is a business plan.
 * This can use one of the static business plan names
 * or the new dynamic price from Stripe. Both parameters are optional.
 *
 * @param {object} params - params object
 * @param {FossaPlanName} params.plan - The fossa plan name to be checked (optional)
 * @param {StripePriceLimit} params.stripePriceLimit - The Stripe price limit to check for (optional)
 */
export const isBusinessTier = (params: FetchPlanInfo | undefined | null): boolean => {
  if (!params || !params.plan) {
    return false;
  }

  if (businessPlans.has(params.plan)) {
    return true;
  }

  if (params.stripePriceLimit && params.plan === params.stripePriceLimit.priceId) {
    return params.stripePriceLimit.type === 'BUSINESS';
  }

  return false;
};

/**
 * Helper function to get the features included in a given tier.
 *
 * Important!! Only use this for static plans. Dynamic plans need to be checked in Stripe.
 */
export const getFeaturesForStaticPlan = (plan: FossaPlanName | undefined): Set<FlagName> =>
  (plan ? TIER_FEATURE_MAP[plan] : new Set<FlagName>()) ?? new Set<FlagName>();

/**
 * Helper function to see if a feature is enabled for a given plan.
 */
export const hasTierFeature = (
  plan: FossaPlanName | undefined,
  flag: FlagName,
  stripePriceLimit?: StripePriceLimit,
): boolean => {
  if (getFeaturesForStaticPlan(plan).has(flag)) {
    return true;
  }

  if (!stripePriceLimit) {
    return false;
  }

  return stripePriceLimit.featureFlags.includes(flag);
};

/**
 * Helper function to see if a plan is the free tier.
 * This can use the static free plan name
 * or the new dynamic price from Stripe. Both parameters are optional.
 *
 * @param {object|null} params - params object
 * @param {FossaPlanName} params.plan - The fossa plan name to be checked (optional)
 * @param {StripePriceLimit} params.stripePriceLimit - The Stripe price limit to check for (optional)
 */
export const isFreeTier = (params: FetchPlanInfo | undefined | null): boolean => {
  if (!params) {
    return false;
  }

  if (!params.plan) {
    return false;
  }

  if (params.plan === FREE_PLAN_NAME) {
    return true;
  }

  if (params.stripePriceLimit && params.plan === params.stripePriceLimit.priceId) {
    return params.stripePriceLimit.type === 'FREE';
  }
  return false;
};

/**
 * Helper function to see if a plan has security. This is checked often enough that it warrants its own helper.
 */
export const isSecurityPlan = (plan: FossaPlanName | undefined) => hasTierFeature(plan, SECURITY);

/**
 * Determines whether or not we're upgrading to/downgrading from a tier that has security. This is needed
 * for deciding whether an org's projects need to be rescanned or not.
 * @param oldStripePlan - The name of the org's current Stripe plan.
 * @param newStripePlan - The name of the Stripe plan the org is changing to.
 * @returns False if neither plan name is the Compliance+Security plan.
 *          False if the org isn't changing tiers.
 *          True if the org is moving to or from the Compliance+Security tier.
 */
export const isSecurityPlanChange = (
  oldStripePlan: FossaPlanName,
  newStripePlan: FossaPlanName,
): boolean => {
  const isNewPlanSecurity = isSecurityPlan(newStripePlan);
  const isOldPlanSecurity = isSecurityPlan(oldStripePlan);
  if (
    (isNewPlanSecurity && isOldPlanSecurity) ||
    (!isNewPlanSecurity && !isOldPlanSecurity)
  ) {
    // Both security or neither are security
    return false;
  }

  // Check to see if the plan tier is staying the same or changing.
  return oldStripePlan !== newStripePlan;
};

export const toValidSeats = (val: number | string): number => {
  let intCount = Number(val);
  if (Number.isNaN(intCount)) {
    return MINIMUM_BUSINESS_SEATS;
  }

  if (intCount < MINIMUM_BUSINESS_SEATS) {
    intCount = MINIMUM_BUSINESS_SEATS;
  }

  while (intCount % MINIMUM_BUSINESS_SEATS_STEP !== 0) {
    intCount += 1;
  }

  if (intCount > MAXIMUM_BUSINESS_SEATS) {
    intCount = MAXIMUM_BUSINESS_SEATS;
  }

  return intCount;
};

export const hasUsedFreeTrial = (customer: Stripe.Customer): boolean => (customer.metadata?.used_free_trial === '1');
export const isUpdatingToFreeTrial = (
  customer: Stripe.Customer,
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  // The organization has already used the free trial
  if (hasUsedFreeTrial(customer)) {
    return false;
  }
  // The free trial is only available when updating from FREE to BUSINESS
  const currentProduct = currentPriceDetails?.product as Stripe.Product;
  const newProduct = newPriceDetails?.product as Stripe.Product;
  const isFree = (currentProduct.metadata?.type === 'FREE' || currentPriceDetails.id === FREE_PLAN_NAME);

  return (isFree && newProduct.metadata?.type === 'BUSINESS');
};

export const isEnterpriseToBusiness = (
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
) => {
  if (!isDynamicPlan(currentPriceDetails.id)) {
    return false;
  }
  const currentProduct = currentPriceDetails?.product as Stripe.Product;
  const newProduct = newPriceDetails?.product as Stripe.Product;
  return (currentProduct.metadata?.type === 'ENTERPRISE' && newProduct.metadata?.type === 'BUSINESS');
};

export const isUpgrading = (
  customer: Stripe.Customer,
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  if (!hasUsedFreeTrial(customer) && isDynamicPlan(currentPriceDetails.id)) {
    return false;
  }

  const newProduct = newPriceDetails?.product as Stripe.Product;
  if (newProduct.metadata?.type === 'FREE') {
    return false;
  }

  return ((currentPriceDetails.unit_amount || 0) <= (newPriceDetails.unit_amount || 0));
};

export const isDowngrading = (
  customer: Stripe.Customer,
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  if (!hasUsedFreeTrial(customer)) {
    return false;
  }

  const currentProduct = currentPriceDetails?.product as Stripe.Product;
  const newProduct = newPriceDetails?.product as Stripe.Product;

  if (currentProduct.metadata?.type !== 'BUSINESS' || newProduct.metadata?.type !== 'BUSINESS') {
    return false;
  }

  if ((currentPriceDetails.recurring?.interval === 'year') && (newPriceDetails.recurring?.interval === 'month')) {
    return true;
  }

  return ((currentPriceDetails.unit_amount || 0) > (newPriceDetails.unit_amount || 0));
};

export const isCancelling = (
  currentPriceDetails: Stripe.Price,
  newPriceDetails: Stripe.Price,
): boolean => {
  const currentProduct = currentPriceDetails?.product as Stripe.Product;
  const newProduct = newPriceDetails?.product as Stripe.Product;
  return (currentProduct.metadata?.type !== 'FREE' && newProduct.metadata?.type === 'FREE');
};

type BusinessTier = 'S' | 'M' | 'L';

/**
 * Represents a business tier size object.
 */
type BusinessTierSize = {
  /**
   * The sizing for S and L. Can be any number of X characters or an empty string if none.
   */
  sizing: string,

  /**
   * The tier. One of S, M or L.
   */
  tier: BusinessTier,

  /**
   * A weight to identify this size. 0 = medium.
   * > 0 is large, the larger the number, the larger the sizing.
   * < 0 is small, the more negative, the smaller the sizing.
   */
  weight: number,
};

/**
 * This function returns a business tier size object that can be used to match and compare
 * business tiers to see which ones are smaller or larger. This function assumes that business
 * tier product names match the following pattern: "Business [SIZE]", where SIZE can be any
 * t-shirt size style string like XL or XXS.
 *
 * Examples:
 * Business [XL] -> { sizing: 'X', tier: 'L', weight: 2 }.
 * Business [M] -> { sizing: '', tier: 'M', weight: 0 }.
 * Business [S] -> { sizing: '', tier: 'S', weight: -1 }.
 * Business [XXXS] -> { sizing: 'XXX', tier: 'S', weight: -4 }.
 *
 * @param productName A product name to get the size for.
 * @returns A BusinessTierSize object or null if the name doesn't match the expected pattern.
 */
export const getBusinessTierSize = (productName: string): BusinessTierSize | null => {
  const match = productName.match(/Business \[(X*)([SML])\]/);
  if (!match) {
    return null;
  }

  const [, sizing, tier] = match;
  if (sizing === undefined || tier === undefined) {
    return null;
  }

  let weight = 0;
  if (tier === 'L') {
    weight = 1 + sizing.length;
  }

  if (tier === 'S') {
    weight = -1 - sizing.length;
  }

  return {
    sizing,
    tier: tier as BusinessTier,
    weight,
  };
};

/**
 * Return true or false if the user is in a business plan tier and updating or downgrading to another tier.
 * If one of the plan names does not match an expected business tier, this function returns null;
 *
 * @returns Null if one of the plan names does not match a business tier name, true or false if the user is downgrading
 * or upgrading respectively.
 */
export const isDowngradingBusinessTier = (currentProductName: string, newProductName: string): boolean | null => {
  const currentProductSize = getBusinessTierSize(currentProductName);
  const newProductSize = getBusinessTierSize(newProductName);

  if (!currentProductSize || !newProductSize) {
    return null;
  }

  return currentProductSize.weight > newProductSize.weight;
};
